import React, { useState, useEffect, useRef } from 'react';
import { Box, Grid, Typography, LinearProgress, useMediaQuery, Button, IconButton, Avatar, Collapse } from '@mui/material';
import BalanceIcon from '@mui/icons-material/Balance';
import RadarChart3 from '../../components/radarChart3'; // Asegúrate de que el componente del gráfico esté implementado.
import theme from '../../styles/StyleTheme'; // Importa tu archivo de estilos.
import { useNavigate, useParams } from 'react-router-dom';
import buildingsBackground from '../../assets/images/fondo-edificiospng.png';
import { publicAxios } from '../../apiClients';
import BackButton from '../../components/BackButton';
import Loading from '../../components/Loading';
import { generateCompanyRouteSegments } from '../../utils/slugify';
import CHMLogo from '../../assets/banners/CM-blanco.png';


const DEFAULT_PROFILE_URL = 'https://i.pinimg.com/474x/15/0f/a8/150fa8800b0a0d5633abc1d1c4db3d87.jpg?nii=t';


const MarketCompany = () => {
    const navigate = useNavigate();
    const { countrySlug, companySlug } = useParams();
    const [firm, setFirm] = useState(null);
    const [persons, setPersons] = useState([]);
    const [showPersons, setShowPersons] = useState(false);
    const personsRef = useRef(null);
    const [firmId, setFirmId] = useState(null);
    const [isResolvingSlug, setIsResolvingSlug] = useState(true);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState(null);

    // Uso de useMediaQuery para detectar pantallas pequeñas
    const isMobile = useMediaQuery('(max-width:600px)'); // Ajusta el breakpoint según necesites

    useEffect(() => {
        window.scrollTo(0, 0);
        setError(null);
        setFirmId(null);
        setFirm(null);
        setPersons([]);
        setIsResolvingSlug(true);
        setIsLoading(true);

        if (!companySlug) {
            const storedCompanyId = localStorage.getItem('companyId');
            if (storedCompanyId) {
                setFirmId(storedCompanyId);
                setIsResolvingSlug(false);
                return;
            }
            setError('Company identifier is missing.');
            setIsResolvingSlug(false);
            setIsLoading(false);
            return;
        }

        const resolveCompanyId = async () => {
            try {
                const response = await publicAxios.get('/institution/market-monitor-corporate/table');
                let strictMatch = null;
                let looseMatch = null;

                response.data.forEach((item) => {
                    const segments = generateCompanyRouteSegments({
                        institutionName: item.institutionName,
                        countryName: item.country?.name ?? item.Country?.name,
                        countryId: item.country?.id ?? item.Country?.id,
                        companyId: item.id,
                    });

                    if (
                        countrySlug &&
                        !strictMatch &&
                        segments.companySlug === companySlug &&
                        segments.countrySlug === countrySlug
                    ) {
                        strictMatch = item;
                    }

                    if (!looseMatch && segments.companySlug === companySlug) {
                        looseMatch = item;
                    }
                });

                const matchedFirm = strictMatch ?? looseMatch;

                if (!matchedFirm) {
                    setError('Unable to find the requested company.');
                    setIsLoading(false);
                    return;
                }

                setFirmId(matchedFirm.id);
                localStorage.setItem('companyId', matchedFirm.id);
            } catch (fetchError) {
                console.error('Error resolving company slug:', fetchError);
                setError('Unable to load the requested company.');
                setIsLoading(false);
            } finally {
                setIsResolvingSlug(false);
            }
        };

        resolveCompanyId();
    }, [companySlug, countrySlug]);

    useEffect(() => {
        if (!firmId) {
            setIsLoading(isResolvingSlug);
            return;
        }
        const fetchFirm = async () => {
            try {
                const response = await publicAxios.get(`/institution/market-monitor-corporate/${firmId}`);
                const firmData = response.data;
                setFirm(firmData);

                localStorage.setItem('companyId', firmId);
            } catch (fetchError) {
                console.error('Error fetching firm data:', fetchError);
                setError('Unable to load the requested company.');
            }
        };
        const fetchPersons = async () => {
            try {
                const response = await publicAxios.get(`/institution/market-monitor-corporate/${firmId}/persons`);
                setPersons(response.data);
            } catch (fetchError) {
                console.error('Error fetching persons data:', fetchError);
            }
        };
        const fetchData = async () => {
            setIsLoading(true);
            try {
                await Promise.all([fetchFirm(), fetchPersons()]);
            } finally {
                setIsLoading(false);
            }
        };
        fetchData();
    }, [firmId, isResolvingSlug]);

    const handleClickBalance = () => {
        navigate('/market-comparative-corporate');
    };

    useEffect(() => {
        if (showPersons && personsRef.current) {
            const timeout = setTimeout(() => {
                personsRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
            }, 300);
            return () => clearTimeout(timeout);
        }
    }, [showPersons]);

    // Estados para controlar la expansión de cada box
    const [isExpanded, setIsExpanded] = useState({
        reputation: false,
        trajectory: false,
        arena: false,
        brandPower: false,
    });

    // Función para manejar el clic en "Ver más"
    const handleSeeMore = (key) => {
        setIsExpanded((prevState) => ({
            ...prevState,
            [key]: !prevState[key],
        }));
    };

    // Textos para cada box
    const boxTexts = {
        reputation:
            "Company’s performance in major industry rankings, such as Merco, among others.",
        brandPower: "The ability of financial institutions to inspire trust, stand out in the market, and build client loyalty through its reputation, expertise, and values.",
        trajectory: 'Recognizes the time that firm’s key professionals have been operating in the market.',
        arena: 'Measures the market conditions in which the firm operates.',
    };

    // Datos para cada box
    const boxes = [
        { key: 'reputation', title: 'Reputation', value: firm?.reputation || 0, gridColumn: '3', gridRow: '1' },
        { key: 'brandPower', title: 'Visibility', value: firm?.brandPower || 0, gridColumn: '4', gridRow: '1' },
        { key: 'trajectory', title: 'Trajectory', value: firm?.trajectory || 0, gridColumn: '3', gridRow: '2' },
        { key: 'arena', title: 'Arena', value: firm?.arena || 0, gridColumn: '4', gridRow: '2' },
    ];
    const groupedPersons = persons.reduce((acc, person) => {
        if (!acc[person.area]) {
            acc[person.area] = [];
        }
        acc[person.area].push(person);
        return acc;
    }, {});

    if (isLoading) {
        return (
            <Loading
                background="#222222"
                barTrackColor="rgba(255, 255, 255, 0.35)"
                barColor="#F5C645"
                logo={CHMLogo}
                logoAlt="Corporate Market Monitor"
            />
        );
    }

    if (error) {
        return (
            <Box sx={{ p: 4 }}>
                <Typography variant="h6" sx={{ color: 'white' }}>
                    {error}
                </Typography>
            </Box>
        );
    }

    return (
        <>
            <Box
                sx={{
                    backgroundImage: `
            linear-gradient(
                rgba(0, 0, 0, 0.8), 
                rgba(0, 0, 0, 0.8)
            ),
            url(https://static.wixstatic.com/media/a4dcb8_875148e2b4cd499fb1991033348f2878~mv2.png/v1/fill/w_2018,h_1643,al_c,q_95,usm_0.66_1.00_0.01,enc_avif,quality_auto/a4dcb8_875148e2b4cd499fb1991033348f2878~mv2.png)
        `,
                    backgroundSize: 'cover',
                    backgroundPosition: 'center',
                    paddingX: isMobile ? '0%' : '5%',
                    // 2. Recomendación: Añadir un color de fondo de respaldo por si la imagen tarda en cargar
                    backgroundColor: 'rgba(0,0,0,0.8)',
                }}
            >
                <Grid container justifyContent="start" sx={{ mt: 1, ml: 0, mb: -3 }}>
                    <BackButton
                        style={{
                            color: '#F5C645',
                        }}

                        path={-1} />
                </Grid>
                <Box sx={{ p: 4 }}>
                    {/* Encabezado */}
                    <Box
                        sx={{
                            backgroundColor: '#222222',
                            borderRadius: 2,
                            boxShadow: '0px 10px 30px rgba(0, 0, 0, 0.1)',
                            padding: 3,
                            marginBottom: 3,
                        }}
                    >
                        <Grid container spacing={2} alignItems="center">
                            {/* Columna 1: Nombre */}
                            <Grid item xs={12} sm={4}>
                                {firm && (
                                    <>
                                        <Typography variant="h5" sx={{ color: 'white', fontWeight: 'bold' }}>
                                            {firm.institutionName}
                                        </Typography>
                                        <Typography variant="subtitle1" sx={{ color: '#F5C645' }}>
                                            {firm?.Country?.name ?? firm?.country?.name}
                                        </Typography>
                                    </>
                                )}
                            </Grid>

                            {/* Columna 2: Otros datos */}
                            <Grid
                                item
                                xs={12}
                                sm={5}
                                sx={{ mt: { xs: 2, sm: 0 }, textAlign: { xs: 'left', sm: 'right' } }}
                            >
                                <Typography variant="body1" sx={{ color: 'white' }}>
                                    Score:{' '}
                                    <span
                                        style={{
                                            color: '#F5C645',
                                            display: 'inline-block',
                                            minWidth: '50px',
                                            textAlign: 'center',
                                            fontWeight: 'bold',
                                        }}
                                    >
                                        {firm?.totalScore ? firm.totalScore.toFixed(2) : 'N/A'}
                                    </span>
                                </Typography>
                                <Typography variant="body1" sx={{ color: 'white' }}>
                                    Local Position:{' '}
                                    <span
                                        style={{
                                            color: '#F5C645',
                                            display: 'inline-block',
                                            minWidth: '50px',
                                            textAlign: 'center',
                                            fontWeight: 'bold',
                                        }}
                                    >
                                        {firm?.localPosition || 'N/A'}
                                    </span>
                                </Typography>
                                <Typography variant="body1" sx={{ color: 'white' }}>
                                    Global Position:{' '}
                                    <span
                                        style={{
                                            color: '#F5C645',
                                            display: 'inline-block',
                                            minWidth: '50px',
                                            textAlign: 'center',
                                            fontWeight: 'bold',
                                        }}
                                    >
                                        {firm?.globalPosition || 'N/A'}
                                    </span>
                                </Typography>
                            </Grid>
                            <Grid item xs={12} sm={3} sx={{ mt: { xs: 2, sm: 0 }, display: 'flex', justifyContent: 'center' }}>
                                <Button
                                    variant="contained"
                                    sx={{
                                        backgroundColor: '#F5C645',
                                        color: '#fff',
                                        '&:hover': { backgroundColor: '#c28e00ff' },
                                        paddingX: '5%',
                                        width: isMobile ? '100%' : '60%',
                                    }}
                                    onClick={() => navigate('/market-comparative-corporate')}
                                >
                                    Compare with another company
                                </Button>
                            </Grid>
                        </Grid>
                    </Box>

                    {/* Grilla principal */}
                    <Grid
                        container
                        spacing={2}
                        sx={{
                            display: 'grid',
                            gridTemplateColumns: isMobile ? '1fr' : 'repeat(4, 1fr)',
                            gridTemplateRows: 'auto auto',
                        }}
                    >
                        {/* Gráfico que ocupa dos columnas y dos filas */}
                        <Grid
                            item
                            sx={{
                                gridColumn: isMobile ? '1' : '1 / span 2',
                                gridRow: isMobile ? '1' : '1 / span 2',
                            }}
                        >
                            <Box
                                sx={{
                                    position: 'relative',
                                    backgroundColor: '#222222',
                                    borderRadius: 2,
                                    boxShadow: '0px 10px 30px rgba(0, 0, 0, 0.1)',
                                    padding: isMobile ? '0px' : '20px',
                                    height: '100%',
                                    minHeight: '400px',
                                }}
                            >
                                <RadarChart3 firm={firm} /> {/* Componente del gráfico */}

                                
                            </Box>
                        </Grid>

                        {/* Boxes con botón "Ver más" */}
                        {boxes.map((box) => (
                            <Grid
                                key={box.key}
                                item
                                sx={{
                                    gridColumn: isMobile ? '1' : box.gridColumn,
                                    gridRow: isMobile ? 'auto' : box.gridRow,
                                }}
                            >
                                <Box
                                    sx={{
                                        backgroundColor: '#4A4A4A',
                                        padding: 2,
                                        borderRadius: 2,
                                        textAlign: 'center',
                                        minHeight: '190px',
                                        maxHeight: '190px',
                                        overflow: 'hidden',
                                        position: 'relative',
                                    }}
                                >
                                    {/* Contenido del box */}
                                    <Box sx={{ position: 'absolute', top: 10, left: 0, right: 0 }}>
                                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                            <Typography
                                                variant="h7"
                                                sx={{
                                                    fontWeight: 'bold',
                                                    fontSize: isExpanded[box.key] ? '0.9rem' : '1rem',
                                                    transition: 'font-size 0.3s ease',
                                                    color: 'white',
                                                }}
                                            >
                                                {box.title}
                                            </Typography>
                                        </Box>
                                        <Typography
                                            variant="h3"
                                            sx={{
                                                color: '#F5C645',
                                                marginY: 0.8,
                                                fontSize: isExpanded[box.key] ? '1.5rem' : '2rem',
                                                transition: 'font-size 0.3s ease',
                                            }}
                                        >
                                            {box.value || 'N/A'}
                                        </Typography>
                                        {!isExpanded[box.key] && (
                                            <Box sx={{ width: '70%', margin: '0 auto', marginTop: 1.5 }}>
                                                <LinearProgress
                                                    variant="determinate"
                                                    value={box.value || 0} // Default to 0 if box.value is undefined
                                                    sx={{
                                                        height: 10,
                                                        borderRadius: 5,
                                                        backgroundColor: '#DBEAF6',
                                                        '& .MuiLinearProgress-bar': { backgroundColor: '#F5C645' },
                                                    }}
                                                />
                                            </Box>
                                        )}
                                    </Box>

                                    {/* Botón "Ver más" */}
                                    <Button
                                        variant="text"
                                        sx={{
                                            position: 'absolute',
                                            bottom: isExpanded[box.key] ? '80px' : '10px',
                                            left: 0,
                                            right: 0,
                                            margin: '0 auto',
                                            textTransform: 'none',
                                            color: 'white',
                                            fontSize: '0.8rem',
                                        }}
                                        onClick={() => handleSeeMore(box.key)}
                                    >
                                        {isExpanded[box.key] ? 'See less' : 'See more'}
                                    </Button>

                                    {/* Texto adicional */}
                                    {isExpanded[box.key] && (
                                        <Typography
                                            variant="body2"
                                            sx={{
                                                position: 'absolute',
                                                bottom: '10px',
                                                left: 10,
                                                right: 10,
                                                fontSize: '0.8rem',
                                                color: 'white',
                                            }}
                                        >
                                            {boxTexts[box.key]}
                                        </Typography>
                                    )}
                                </Box>
                            </Grid>
                        ))}
                    </Grid>
                </Box>

                {persons.length > 0 && (
                    <>
                        {/* Boton see more + tabla */}
                        {/* <Box sx={{ textAlign: 'center', mt: 4 }}>
                            <Button
                                variant="contained"
                                sx={{
                                    backgroundColor: '#F5C645',
                                    color: '#fff',
                                    '&:hover': { backgroundColor: '#c28e00ff' },
                                    textTransform: 'none',
                                    mb: 3,
                                }}
                                onClick={() => setShowPersons((prev) => !prev)}
                            >
                                {showPersons ? 'See Less' : 'See More'}
                            </Button>
                        </Box> */}
                        <Collapse in={showPersons} timeout="auto" unmountOnExit>
                            <Box
                                ref={personsRef}
                                sx={{
                                    mt: 2,
                                    px: 4,
                                    py: 2, // 👈 agrega espacio arriba y abajo
                                    backgroundColor: '#4A4A4A',
                                    borderRadius: 2,
                                    width: '100%',
                                    maxWidth: 600,
                                    margin: '0 auto',
                                }}
                            >
                                {Object.entries(groupedPersons).map(([area, people]) => (
                                    <Box key={area} sx={{ mb: 2 }}>
                                        <Typography variant="h6" sx={{ mb: 1, color: 'white', fontWeight: 'bold' }}>
                                            {area}
                                        </Typography>
                                        {people.map((person) => (
                                            <Box
                                                key={person.id}
                                                sx={{
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    justifyContent: 'space-between',
                                                    mb: 1,
                                                }}
                                            >
                                                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                                    <Avatar
                                                        src={person.url_foto || DEFAULT_PROFILE_URL}
                                                        alt={`${person.nombre} ${person.apellido_paterno}`}
                                                        sx={{ width: 56, height: 56, mr: 2 }}
                                                    />
                                                    <Typography variant="body1" sx={{ color: 'white' }}>
                                                        {person.nombre} {person.apellido_paterno} {person.apellido_materno}
                                                    </Typography>
                                                </Box>
                                                <Typography variant="body2" sx={{ color: 'white', fontWeight: 'bold' }}>
                                                    {person.calificacion}
                                                </Typography>
                                            </Box>
                                        ))}
                                    </Box>
                                ))}
                            </Box>

                        </Collapse>
                    </>
                )}


                {/* Contacto */}
                <Box
                    sx={{
                        backgroundColor: 'none',
                        paddingTop: 4,
                        paddingBottom: 13,
                        paddingX: 8,
                        borderRadius: 'none',
                        display: 'flex',
                        flexDirection: isMobile ? 'column' : 'row',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                    }}
                >
                    <Box sx={{ flexGrow: 1, textAlign: isMobile ? 'center' : 'left' }}>
                        <Typography variant="body1" sx={{ color: 'white', marginBottom: 1 }}>
                            Our AI model is fed with information coming from public sources or provided by the companies (in this case, verified by our team). Are you a member in this place and want to update displayed information?
                        </Typography>
                    </Box>
                    <Box sx={{ ml: isMobile ? 0 : 4, mt: isMobile ? 2 : 0 }}>
                        <Button
                            component="a"
                            href="https://thelegalindustry.com/contact-us/"
                            target="_blank"
                            rel="noopener noreferrer"
                            variant="contained"
                            sx={{
                                backgroundColor: '#F5C645',
                                color: '#fff',
                                borderRadius: 4,
                                width: '100%',
                                height: 30,
                                textTransform: 'none',
                                whiteSpace: 'nowrap',
                                '&:hover': { backgroundColor: '#c28e00ff' },
                            }}
                        >
                            Contact us
                        </Button>
                    </Box>
                </Box>
            </Box>
        </>
    );
};

export default MarketCompany;
